import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	Invoice,
	ListInvoicesResponse,
	ListRentalInvoicesParams,
	UpdateInvoiceBody,
} from './types/finance'

export const invoicesApi = createApi({
	reducerPath: 'invoicesApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['Invoice', 'RentalInvoice', 'FinanceInvoice'],
	endpoints: (builder) => ({
		getInvoice: builder.query<Invoice | null, string>({
			query: (id) => `/invoices/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'Invoice', id }],
		}),

		updateInvoice: builder.mutation<Invoice | null, { id: string; body: UpdateInvoiceBody }>({
			query: ({ id, body }) => ({ url: `/invoices/${id}`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Invoice', id },
				{ type: 'RentalInvoice' }, // Invalidate ALL rental invoices lists (matches all rental IDs)
				'FinanceInvoice', // Invalidate finance list so status updates appear immediately
			],
		}),

		deleteInvoice: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/invoices/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'Invoice', id },
				{ type: 'RentalInvoice' }, // Invalidate ALL rental invoices lists (matches all rental IDs)
				'FinanceInvoice', // Invalidate finance list for both pages
			],
		}),

		listRentalInvoices: builder.query<ListInvoicesResponse, ListRentalInvoicesParams>({
			query: ({ rentalId, ...params }) => ({
				url: `/rent/${rentalId}/invoices`,
				params,
			}),
			providesTags: (_r, _e, { rentalId }) => [{ type: 'RentalInvoice', id: rentalId }],
		}),

		downloadInvoicePdf: builder.mutation<Blob, { id: string; format: string }>({
			query: ({ id, format }) => ({
				url: `/invoices/${id}/download`,
				params: { format },
				responseHandler: async (response) => {
					if (!response.ok) throw new Error(`Failed to download PDF: ${response.statusText}`)
					return response.blob()
				},
			}),
		}),
	}),
})

export const {
	useGetInvoiceQuery,
	useUpdateInvoiceMutation,
	useDeleteInvoiceMutation,
	useListRentalInvoicesQuery,
	useDownloadInvoicePdfMutation,
} = invoicesApi
/** Alias preserving the old web hook naming. */
export const useListInvoicesQuery = invoicesApi.useListRentalInvoicesQuery
